import config from '../../../config'
import { authHeader, customFetch, handleResponse, commonRequestOptions } from '../../../helpers'
import { appName } from '../appConfig'

const base = `${appName}/event`

export const eventService = {
  getAll,
  getAllUnbounded,
  getByRange,
  getBySlug,
  getInvitePreview,
  getUsers,
  create,
  update,
  remove,
  toggleInterest,
  setAttendees,
}

function getAll(daysAhead, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}?daysAhead=${daysAhead}`, opts, signal).then(handleResponse)
}

// Every event, no date bound at all — used by the full /list page, which
// should never hide an event just because it falls outside some rolling
// window (unlike the widget/calendar, which intentionally stay windowed).
function getAllUnbounded(signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}?all=true`, opts, signal).then(handleResponse)
}

// start/end are Date objects (or ISO strings) — used by the calendar's
// week/month views, which need an arbitrary window rather than "upcoming
// from now".
function getByRange(start, end, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  const params = new URLSearchParams({ start: new Date(start).toISOString(), end: new Date(end).toISOString() })
  return customFetch(`${config.api.url}/${base}?${params.toString()}`, opts, signal).then(handleResponse)
}

function getBySlug(slug, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/${slug}`, opts, signal).then(handleResponse)
}

// Renders the invitation email exactly as a guest would receive it ({subject, html}),
// using a sample guest so merge tags show example data. Read-only — sends nothing.
function getInvitePreview(slug, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/${slug}/invite_preview`, opts, signal).then(handleResponse)
}

function create(payload, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}`, opts, signal).then(handleResponse)
}

function update(slug, payload, signal) {
  const opts = {
    method:  'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${slug}`, opts, signal).then(handleResponse)
}

function remove(slug, signal) {
  const opts = {
    method:  'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${slug}`, opts, signal).then(handleResponse)
}

function toggleInterest(slug, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${slug}/interest`, opts, signal).then(handleResponse)
}

function getUsers(signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/users`, opts, signal).then(handleResponse)
}

// Replaces the event's full attendee list (id array) — distinct from
// toggleInterest, which only ever affects the calling user's own interest.
function setAttendees(slug, userIds, signal) {
  const opts = {
    method:  'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({ userIds }),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${slug}/attendees`, opts, signal).then(handleResponse)
}
